import React, {useCallback, useContext, useEffect, useRef, useState} from "react"
import RefreshTimer from "../../util/RefreshTimer";
import {AslDeviceOverviewContext} from "./context/AslDeviceOverviewContext";
import Row from "@meridian/components/row";
import {getRefreshInterval} from "../../util/Util";
import {FormattedMessage, injectIntl} from "react-intl";
import Button from "@amzn/meridian/button";
import Menu, {MenuItem} from "@amzn/meridian/menu";
import AddAslSingleDeviceModal from "./modal/AddAslSingleDeviceModal";
import Icon from "@amzn/meridian/icon";
import Column from "@amzn/meridian/column";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import {ACTIVE, BLUE_COLOR, ORANGE_COLOR} from "../../util/Constants";
import Text from "@amzn/meridian/text";
import Box from "@amzn/meridian/box";
import Loader from "../../loader/Loader";
import Tooltip from "@amzn/meridian/tooltip";
import chevronDownLargeTokens from "@amzn/meridian-tokens/base/icon/chevron-down-large";
import Alert from "@amzn/meridian/alert";
import DeleteSingleDeviceModal from "../../commonui/DeleteSingleDeviceModal";
import AddEditViaJsonModal from "../../commonui/AddEditViaJsonModal";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";

const AslDeviceOverview = ({intl}) => {

    const {viewModel, dispatch} = useContext(AslDeviceOverviewContext);
    const [openActionsMenu, setOpenActionsMenu] = useState({});
    const [openAddModal, setOpenAddModal] = useState(false)
    const [selected, setSelected] = useState("")
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const actionButtonRefs = useRef([]);
    const tableEndRef = useRef(null)

    const onClickActionsButton = useCallback((index) => {
        let _open = {...openActionsMenu};
        _open[index] = true;
        setOpenActionsMenu(_open);
    }, []);
    const onCloseActions = useCallback((index) => {
        let _open = {...openActionsMenu};
        _open[index] = false;
        setOpenActionsMenu(_open)
    }, []);

    useEffect(() => {
        getData()
        const interval = setInterval(() => {
            if (!document.hidden) {
                getData();
            }
        }, getRefreshInterval());
        return () => clearInterval(interval);
    }, []);
    useEffect(() => {
        actionButtonRefs.current = actionButtonRefs.current.slice(0, viewModel.aslDeviceInformation.length);
    }, [viewModel.aslDeviceInformation]);

    const onSort = (sortDetails) => {
        dispatch({
            type: 'SORT_DATA',
            data: {column: sortDetails.sortColumn, direction: sortDetails.sortDirection}
        })
    }
    const sortedDeviceList = Object.values(viewModel.aslDeviceInformation);
    sortedDeviceList && sortedDeviceList.sort((a, b) => {
        if (a[viewModel.sortPref.column] < b[viewModel.sortPref.column]) {
            return viewModel.sortPref.direction === "ascending" ? -1 : 1
        }
        if (a[viewModel.sortPref.column] > b[viewModel.sortPref.column]) {
            return viewModel.sortPref.direction === "ascending" ? 1 : -1
        }
        return 0
    })

    const getData = () => {
        setOpenAddModal(false)
        setOpenDeleteModal(false)
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }
    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }
    const onClickAddEditDevice = () => {
        setSelected("");
        setOpenAddModal(true);
    }
    const onClickEditDevice = (mac) => {
        setSelected(mac)
        setOpenAddModal(true)
    }
    const onClickRemove = (mac) => {
        setSelected(mac)
        setOpenDeleteModal(true)
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    } else {
        return (
            <Row widths="100%"
                 alignmentVertical="top"
                 spacingInset="none none large none">
                <Column>
                    <Row width="100%" widths={["100", "fill", "100"]}>
                        <RefreshTimer deps={[viewModel]}/>
                        <Box></Box>
                        <Tooltip position="top" title={intl.formatMessage({
                            id: 'tooltip.scrolltobottom',
                            defaultMessage: 'Scroll to bottom'
                        })}>
                            <Button onClick={scrollToBottom}
                                    type="secondary"
                                    size="small">
                                <Icon tokens={chevronDownLargeTokens}/>
                            </Button>
                        </Tooltip>
                    </Row>
                    {sortedDeviceList.length === 0 ?
                        <Alert type="informational" size="small">
                            <FormattedMessage id="alert.device"
                                              defaultMessage="No device present for this station."/>
                        </Alert> :
                        <Table
                            headerRows={1}
                            spacing="small"
                            sortColumn={viewModel.sortPref.column}
                            sortDirection={viewModel.sortPref.direction}
                            onSort={onSort}
                            showDividers={true}
                            showStripes={true}>
                            <TableRow
                                highlightOnHover={false}>
                                <TableCell sortColumn="name">
                                    <FormattedMessage id="device.name" defaultMessage="Name"/>
                                </TableCell>
                                <TableCell sortColumn="mac">
                                    <FormattedMessage id="device.mac_ip" defaultMessage="MAC/IP"/>
                                </TableCell>
                                <TableCell sortColumn="connection">
                                    <FormattedMessage id="device.connection" defaultMessage="Connection Status"/>
                                </TableCell>
                                <TableCell>
                                    <FormattedMessage id="device.action" defaultMessage="Action"/>
                                </TableCell>
                            </TableRow>
                            {sortedDeviceList.map((device, index) => (
                                <TableRow key={index}
                                          highlightOnHover={true}>
                                    <TableCell>
                                        <b>{device.name}</b>
                                        <br></br>
                                        {device.deviceType}
                                    </TableCell>
                                    <TableCell>
                                        <Column>
                                            <b>{device.mac}</b>
                                            {device.ip}
                                        </Column>
                                    </TableCell>
                                    <TableCell>
                                        {device.assetStatus ?
                                            <p style={{color: device.assetStatus.toUpperCase() === ACTIVE ? BLUE_COLOR : ORANGE_COLOR}}>
                                                <b>{device.assetStatus}</b>
                                            </p> :
                                            <FormattedMessage id="loading.text" defaultMessage="Loading..."/>}
                                    </TableCell>
                                    <TableCell>
                                        <Text type="b300">
                                            <Button ref={el => actionButtonRefs.current[index] = el}
                                                    type="icon"
                                                    size="medium"
                                                    onClick={() => onClickActionsButton(index)}
                                                    aria-controls="my-menu"
                                                    aria-haspopup="menu"
                                                    aria-expanded="false">
                                                ...
                                            </Button>
                                            <Menu anchorNode={actionButtonRefs.current[index]}
                                                  open={openActionsMenu[index] ? openActionsMenu[index] : false}
                                                  position="left"
                                                  onClose={() => onCloseActions(index)}>
                                                <MenuItem onClick={() => onClickEditDevice(device.mac)}>
                                                    <FormattedMessage id="actions.addDevice"
                                                                      defaultMessage="Edit Device"/>
                                                </MenuItem>
                                                <MenuItem onClick={() => onClickRemove(device.mac)}>
                                                    <FormattedMessage id="actions.deleteDevice"
                                                                      defaultMessage="Delete Device"/>
                                                </MenuItem>
                                            </Menu>
                                        </Text>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </Table>}
                    <Row width="100%" widths={["100", "100", "fill", "100"]}>
                        <Button onClick={onClickAddEditDevice} type="primary" size="small">
                            <Icon tokens={plusCircleTokens}/>
                            <FormattedMessage id="device.add_device_button_modal" defaultMessage="Add Device Configuration"/>
                        </Button>
                        <AddEditViaJsonModal/>
                        <Box></Box>
                        <Button onClick={() => setOpenDeleteModal(true)} type="tertiary" size="small">
                            <Icon tokens={trashTokens}/>
                            <FormattedMessage id="button.delete" defaultMessage="Delete device"/>
                        </Button>
                    </Row>
                    <AddAslSingleDeviceModal open={openAddModal} setOpen={setOpenAddModal}
                                             selected={selected} setSelected={setSelected}/>
                    <DeleteSingleDeviceModal open={openDeleteModal} setOpen={setOpenDeleteModal}
                                             selected={selected}
                                             macAddressArray={viewModel.macAddressArray} dispatch={dispatch}/>
                    <div ref={tableEndRef}/>
                </Column>
            </Row>
        );
    }
}

export default injectIntl(AslDeviceOverview)



// WEBPACK FOOTER //
// ./src/components/devicemanagement/asldevice/AslDeviceOverview.js